import { jsPDF } from "jspdf";
import { QUOTE } from "@/data/quote";
import { CONTACT } from "@/data/site";

const NAVY = [29, 53, 87];
const STEEL = [52, 91, 115];
const WINE = [122, 43, 51];
const GREEN = [42, 75, 60];
const INK = [26, 26, 26];
const GREY = [92, 92, 92];
const LOGO_URL = "/assets/682gladr_logo.png";

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const fmtDate = (iso, lang) => new Date(iso + "T00:00:00").toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" });

const toDataUrl = (img, mime, q) => {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
  canvas.getContext("2d").drawImage(img, 0, 0);
  return canvas.toDataURL(mime, q);
};

export async function generateQuotePdf({ lang = "it", roomLabel, roomsCount = 1, rooms = [], arrival, departure, nights, guests, total, currency = "EUR" }) {
  const Q = QUOTE[lang] || QUOTE.it;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = 595, H = 842, M = 48;
  const bandH = 62;
  const contentBottom = H - bandH - 16; // reserve footer area on every page
  const cur = currency === "EUR" ? "€" : currency + " ";

  let y = 138;
  const ensureSpace = (needed) => {
    if (y + needed > contentBottom) { doc.addPage(); y = 60; }
  };

  // ---- Header (white background) ----
  try {
    const img = await loadImage(LOGO_URL);
    const data = toDataUrl(img, "image/png");
    const h = 62;
    const w = h * (img.naturalWidth / img.naturalHeight);
    doc.addImage(data, "PNG", M, 34, w, h);
  } catch (e) { /* logo optional */ }
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold"); doc.setFontSize(21);
  doc.text(Q.docTitle, W - M, 52, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...STEEL);
  doc.text(Q.subtitle, W - M, 70, { align: "right" });
  const today = new Date().toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" });
  doc.setTextColor(...GREY); doc.setFontSize(8.5);
  doc.text(`${Q.generatedOn} ${today}`, W - M, 86, { align: "right" });
  doc.setDrawColor(...NAVY); doc.setLineWidth(1.5);
  doc.line(M, 108, W - M, 108);

  // ---- Summary + price box ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...NAVY);
  doc.text(Q.summaryTitle, M, y); y += 12;
  const rows = [
    [Q.lblDates, `${fmtDate(arrival, lang)} – ${fmtDate(departure, lang)}`],
    [Q.lblNights, `${nights} ${nights === 1 ? Q.night : Q.nights}`],
    [Q.lblGuests, `${guests} ${guests === 1 ? Q.guest : Q.guests}`],
    [roomsCount > 1 ? Q.lblRooms : Q.lblRoom, roomLabel],
  ];
  const rowH = 22;
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.8);
  const taxLines = doc.splitTextToSize(Q.taxNote, W - 2 * M - 32);
  const boxH = rows.length * rowH + 42 + taxLines.length * 10 + 8;
  const boxTop = y;
  doc.setDrawColor(...NAVY); doc.setLineWidth(1);
  doc.setFillColor(247, 245, 242);
  doc.roundedRect(M, boxTop, W - 2 * M, boxH, 8, 8, "FD");
  let ry = boxTop + 20; doc.setFontSize(10.5);
  rows.forEach(([label, value]) => {
    doc.setFont("helvetica", "normal"); doc.setTextColor(...GREY);
    doc.text(label, M + 18, ry);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...INK);
    doc.text(String(value), W - M - 18, ry, { align: "right" });
    ry += rowH;
  });
  doc.setDrawColor(...NAVY); doc.setLineWidth(0.7); doc.line(M + 14, ry - 4, W - M - 14, ry - 4);
  doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(...GREY);
  doc.text(Q.lblTotal + " *", M + 18, ry + 14);
  doc.setFontSize(18); doc.setTextColor(...NAVY);
  doc.text(total != null ? `${cur}${Math.round(total)}` : "—", W - M - 18, ry + 16, { align: "right" });
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.8); doc.setTextColor(...WINE);
  doc.text(taxLines, M + 18, ry + 34);
  y = boxTop + boxH + 26;

  // ---- Useful information (shared) ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...WINE);
  doc.text(Q.usefulTitle, M, y); y += 16;
  doc.setFontSize(9); doc.setTextColor(...INK);
  Q.useful.forEach((it) => {
    doc.setFillColor(...WINE); doc.circle(M + 3, y - 3, 1.7, "F");
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(it, W - 2 * M - 16);
    doc.text(lines, M + 14, y);
    y += lines.length * 11 + 5;
  });
  y += 14;

  // ---- Per-room blocks: photo + description + own services ----
  const iw = 140, ih = 94;
  for (const r of rooms) {
    // Precompute description lines and service columns to size the block.
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.7);
    const descLines = doc.splitTextToSize(r.description || "", W - M - (M + iw + 16));
    const photoBlockH = Math.max(ih, descLines.length * 10 + 12);

    const services = r.services || [];
    const colW = W / 2 - M - 20;
    const half = Math.ceil(services.length / 2);
    const cols = [services.slice(0, half), services.slice(half)];
    doc.setFontSize(9);
    let svcH = 0;
    cols.forEach((items) => {
      let ch = 0;
      items.forEach((it) => { ch += doc.splitTextToSize(it, colW).length * 11 + 6; });
      if (ch > svcH) svcH = ch;
    });
    const blockH = 20 + photoBlockH + 12 + 16 + svcH + 16;
    ensureSpace(blockH);

    // Room name heading
    doc.setFont("helvetica", "bold"); doc.setFontSize(12.5); doc.setTextColor(...NAVY);
    doc.text(r.label, M, y); y += 18;

    // Photo (left) + description (right)
    const rowTop = y;
    if (r.image) {
      try {
        const rimg = await loadImage(r.image);
        const rdata = toDataUrl(rimg, "image/jpeg", 0.82);
        doc.addImage(rdata, "JPEG", M, rowTop, iw, ih);
        doc.setDrawColor(...NAVY); doc.setLineWidth(0.8); doc.rect(M, rowTop, iw, ih);
      } catch (e) { /* photo optional */ }
    }
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.7); doc.setTextColor(...GREY);
    let dy = rowTop + 10;
    descLines.forEach((ln) => { doc.text(ln, M + iw + 16, dy); dy += 10; });
    y = rowTop + photoBlockH + 12;

    // Room services heading + two columns
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...GREEN);
    doc.text(Q.servicesTitle, M, y); y += 16;
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
    const colX = [M + 14, W / 2 + 8];
    const colDotX = [M + 3, W / 2 - 3];
    const startY = y;
    let maxY = y;
    cols.forEach((items, ci) => {
      let cyy = startY;
      items.forEach((it) => {
        doc.setFillColor(...GREEN); doc.circle(colDotX[ci], cyy - 3, 1.7, "F");
        const lines = doc.splitTextToSize(it, colW);
        doc.text(lines, colX[ci], cyy);
        cyy += lines.length * 11 + 6;
      });
      if (cyy > maxY) maxY = cyy;
    });
    y = maxY + 16;
  }

  // ---- Footer band (navy) on the LAST page only ----
  doc.setFillColor(...NAVY);
  doc.rect(0, H - bandH, W, bandH, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.text(Q.footer, M, H - bandH + 22);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(210, 220, 232);
  doc.text(`${CONTACT.phoneDisplay}   ·   ${CONTACT.email}   ·   ilakeitbellagio.it`, M, H - bandH + 38);
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(180, 195, 212);
  doc.text(Q.validity, M, H - bandH + 52);

  const prefix = { it: "preventivo", en: "quote", fr: "devis" }[lang] || "preventivo";
  const safe = String(roomLabel || prefix).replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  doc.save(`${prefix}-ilakeit-${safe}-${arrival}.pdf`);
}
